/**
 * Live Gym Board Helper Utilities
 * 
 * Provides reusable functions for Live Board components:
 * - Set/exercise progress calculations
 * - Time formatting
 * - State management
 */

/**
 * Get the current set index (1-based) for an exercise
 * @param {Object} exercise - The exercise object with actual_sets and total_sets
 * @returns {Object} { current: number, total: number }
 */
export const getCurrentSetProgress = (exercise) => {
  if (!exercise) return { current: 1, total: 1 };
  
  const completedSets = exercise.completed_sets || 0;
  const totalSets = exercise.total_sets || 1;
  
  // Current set is completedSets + 1 (the one they're working on)
  // Unless all sets are done, then show the last set
  const currentSet = completedSets >= totalSets ? totalSets : completedSets + 1;
  
  return {
    current: currentSet,
    total: totalSets,
    isComplete: completedSets >= totalSets
  };
};

/**
 * Get the next exercise in a workout
 * @param {Array} exercises - Array of exercises
 * @param {number} currentIndex - Current exercise index (0-based)
 * @returns {Object|null} Next exercise object or null if last
 */
export const getNextExercise = (exercises, currentIndex) => {
  if (!exercises || !Array.isArray(exercises)) return null;
  
  const nextIndex = currentIndex + 1;
  if (nextIndex >= exercises.length) return null;
  
  return exercises[nextIndex];
};

/**
 * Calculate time since last completed set
 * @param {Object} exercise - Current exercise with sets array
 * @returns {Object|null} { seconds: number, display: string } or null
 */
export const getTimeSinceLastSet = (exercise) => {
  if (!exercise?.sets) return null;
  
  // Find the last completed set with a timestamp
  const completedSets = exercise.sets.filter(
    s => s.status === 'completed' || s.status === 'last_performed'
  );
  
  if (completedSets.length === 0) return null;
  
  // The last performed set would be the most recent
  const lastSet = completedSets[completedSets.length - 1];
  
  // Check if we have completed_at data (passed from backend)
  if (!lastSet.completed_at) return null;
  
  try {
    const completedTime = new Date(lastSet.completed_at);
    const now = new Date();
    const diffSeconds = Math.floor((now - completedTime) / 1000);
    
    if (diffSeconds < 0) return null;
    
    return {
      seconds: diffSeconds,
      display: formatTimeDuration(diffSeconds)
    };
  } catch {
    return null;
  }
};

/**
 * Format seconds into a readable duration string
 * @param {number} totalSeconds - Duration in seconds
 * @returns {string} Formatted string like "1:18" or "2:05"
 */
export const formatTimeDuration = (totalSeconds) => {
  if (typeof totalSeconds !== 'number' || totalSeconds < 0) return '0:00';
  
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Format rest timer display — ELAPSED (counts up, matches the intra-workout
 * UX). Falls back to legacy `display` (remaining countdown) only when the
 * backend hasn't been updated.
 * @param {Object} restTimer - Rest timer object from backend
 * @returns {string} Formatted time string
 */
export const formatRestTimer = (restTimer) => {
  if (!restTimer) return '0:00';

  // New (elapsed-first) field set
  if (restTimer.elapsed_display) return restTimer.elapsed_display;
  if (typeof restTimer.elapsed === 'number') return formatTimeDuration(restTimer.elapsed);

  // Legacy fallback (countdown / remaining)
  if (restTimer.display) return restTimer.display;
  const remaining = restTimer.remaining || 0;
  return formatTimeDuration(remaining);
};

/**
 * Format the recommended target duration (secondary line on the Live Board).
 * Returns null when the backend didn't expose it (legacy mode).
 */
export const formatRestTarget = (restTimer) => {
  if (!restTimer) return null;
  if (restTimer.target_display) return restTimer.target_display;
  if (typeof restTimer.total === 'number') return formatTimeDuration(restTimer.total);
  return null;
};

/**
 * Get display state label (replacing ambiguous IDLE)
 * @param {string} backendState - State from backend
 * @param {Object} restTimer - Rest timer object
 * @returns {string} Display-friendly state label
 */
export const getDisplayState = (backendState, restTimer) => {
  // If actively resting, show RESTING
  if (restTimer && restTimer.remaining > 0) {
    return 'RESTING';
  }
  
  // Map backend states to display states
  switch (backendState) {
    case 'RESTING':
      return 'RESTING';
    case 'READY':
      return 'READY';
    case 'ACTIVE':
      return 'ACTIVE';
    case 'IDLE':
      // IDLE is ambiguous - replace with READY (waiting to start next set)
      return 'READY';
    default:
      return 'READY';
  }
};

/**
 * Get set status for display
 * @param {Object} set - Set object
 * @param {number} index - Set index
 * @param {number} completedCount - Number of completed sets
 * @returns {string} 'completed' | 'current' | 'pending'
 */
export const getSetDisplayStatus = (set, index, completedCount) => {
  if (set.status === 'completed') return 'completed';
  if (set.status === 'last_performed') return 'current';
  if (index === completedCount) return 'current';
  return 'pending';
};

/**
 * Format set display string
 * @param {Object} set - Set object with reps and weight
 * @returns {string} Formatted string like "9x50"
 */
export const formatSetDisplay = (set) => {
  const reps = set?.reps || 0;
  const weight = set?.weight || 0;
  return `${reps}×${weight}`;
};

/**
 * Format a cardio round chip (e.g. "1:30" for a completed round, or "—:—"
 * for a pending round when no target time has been configured).
 * Cardio data lives in `set.time` (elapsed seconds for that round).
 */
export const formatRoundDisplay = (set) => {
  const t = set?.time;
  if (typeof t !== 'number' || t <= 0) return '—:—';
  return formatTimeDuration(t);
};

export default {
  getCurrentSetProgress,
  getNextExercise,
  getTimeSinceLastSet,
  formatTimeDuration,
  formatRestTimer,
  formatRestTarget,
  getDisplayState,
  getSetDisplayStatus,
  formatSetDisplay,
  formatRoundDisplay,
};
